import React, { useState } from 'react';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import {
	useRejectRegistryMutation,
	RejectRegistryInput,
	RejectRegistryMutation,
	Registry
} from '@/graphql/__generated__/onboarding';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import logger from '@/logger';
import toast from 'react-hot-toast';
import CreditBureauSummary from './CreditBureauSummary';

const RejectRegistryModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	clientId: string;
	onSuccess?: () => void;
	registry: Registry;
}> = ({ isOpen, onClose, clientId, onSuccess, registry }) => {
	const t = useTranslations('RegistryDetails');
	const [formData, setFormData] = useState<RejectRegistryInput>({
		clientId: '',
		deliberation: ''
	});
	const [formErrors, setFormErrors] = useState<Record<keyof RejectRegistryInput, string>>({
		clientId: '',
		deliberation: ''
	});

	const onboardingClient = useApolloClient(SCHEMAS.ONBOARDING);

	const [rejectRegistry, { loading }] = useRejectRegistryMutation({
		client: onboardingClient,
		onCompleted: async (data: RejectRegistryMutation) => {
			const rejectResult = data.rejectRegistry;
			if (rejectResult?.successful) {
				logger.success('Registry rejected successfully', rejectResult);
				onClose();
				if (onSuccess) {
					onSuccess();
				}
			}
			if (rejectResult?.mutationErrors && rejectResult.mutationErrors.length > 0) {
				let hasFieldErrors = false;

				rejectResult.mutationErrors.forEach((error) => {
					if (error) {
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								if (error.field === 'deliberation') {
									setFormErrors((prev) => ({
										...prev,
										deliberation: error.message || t('DeliberationRequired')
									}));
									hasFieldErrors = true;
								}
								break;
							case 'InvalidAction':
								logger.error('Invalid action', error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});

				if (!hasFieldErrors) {
					toast.error(t('ErrorRejectingRegistry'));
				}
			}
		},
		onError: (error) => {
			logger.error('Error rejecting registry', error);
			toast.error(t('ErrorRejectingRegistry'));
		}
	});

	// Set initial client ID
	React.useEffect(() => {
		if (clientId) {
			setFormData((prev) => ({
				...prev,
				clientId
			}));
		}
	}, [clientId]);

	const handleDeliberationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setFormData((prev) => ({
			...prev,
			deliberation: value
		}));

		if (formErrors.deliberation) {
			setFormErrors((prev) => ({
				...prev,
				deliberation: ''
			}));
		}
	};

	const handleRejectRegistry = async () => {
		setFormErrors({
			clientId: '',
			deliberation: ''
		});
		await rejectRegistry({
			variables: {
				input: formData
			}
		});
	};

	const deliberationLength =
		t('DeliberationLength') + '. ' + formData.deliberation.length + '/3400';

	return (
		<ConfirmModal
			isOpen={isOpen}
			confirmActionText={t('Reject')}
			title={t('RejectRegistryTitle')}
			onClose={onClose}
			onCancel={onClose}
			onConfirm={handleRejectRegistry}
			maxWidth={600}
			subtitle={t('RejectRegistrySubtitle')}
			confirmBtnLoading={loading}
		>
			<div className="flex flex-col gap-4 py-4">
				<CreditBureauSummary registry={registry} />
				<Typography size="md">{t('DeliberationLabel')}</Typography>
				<div className="flex flex-col gap-1">
					<TextArea
						rows={4}
						value={formData.deliberation}
						onChange={handleDeliberationChange}
						placeholder={t('EnterRejectionReason')}
						error={!!formErrors.deliberation}
						hint={deliberationLength}
						maxChars={3400}
						strictMaxChars
					/>
					{formErrors.deliberation && (
						<Typography size="sm" color="error">
							{formErrors.deliberation}
						</Typography>
					)}
				</div>
			</div>
		</ConfirmModal>
	);
};

export default RejectRegistryModal;
